package fr.arcplex.network.cache

import fr.arcplex.network.http.Serializer
import fr.arcplex.network.logging.Logger
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import kotlinx.serialization.json.put

class ResponseCache(
    private val storage: CacheStorage = createCacheStorage(),
    val ttlMillis: Long = 600_000
) {
    private data class Entry(val body: String, val expiresAt: Long)
    private val memory = LinkedHashMap<String, Entry>()
    private val maxMemoryEntries = 128
    private val mutex = Mutex()

    suspend fun get(key: String): String? = mutex.withLock {
        val now = currentTimeMillis()
        memory[key]?.let { entry ->
            if (entry.expiresAt > now) return entry.body
            memory.remove(key)
        }
        storage.read(key)?.let { raw ->
            val entry = parseEntry(raw, now)
            if (entry != null) {
                putMemory(key, entry)
                return entry.body
            }
            storage.delete(key)
        }
        null
    }

    suspend fun put(key: String, body: String) = mutex.withLock {
        val expiresAt = currentTimeMillis() + ttlMillis
        putMemory(key, Entry(body, expiresAt))
        try {
            storage.write(key, encodeEntry(body, expiresAt))
            cleanExpired(currentTimeMillis())
        } catch (e: Exception) {
            Logger.e("ResponseCache", "disk cache write failed: ${e.message}")
        }
        Unit
    }

    suspend fun remove(key: String) = mutex.withLock {
        memory.remove(key)
        try {
            storage.delete(key)
        } catch (e: Exception) {
        }
        Unit
    }

    suspend fun clear() = mutex.withLock {
        memory.clear()
        try {
            storage.listKeys().forEach { storage.delete(it) }
        } catch (e: Exception) {
        }
        Unit
    }

    private fun putMemory(key: String, entry: Entry) {
        memory[key] = entry
        while (memory.size > maxMemoryEntries) {
            memory.remove(memory.keys.first())
        }
    }

    private fun parseEntry(raw: String, now: Long): Entry? = try {
        (Serializer.json.parseToJsonElement(raw) as? JsonObject)?.let { obj ->
            val expiresAt = obj["expiresAt"]?.jsonPrimitive?.longOrNull ?: 0L
            val body = obj["body"]?.jsonPrimitive?.contentOrNull
            if (body != null && expiresAt > now) Entry(body, expiresAt) else null
        }
    } catch (e: Exception) {
        null
    }

    private suspend fun cleanExpired(now: Long) {
        val keys = try {
            storage.listKeys()
        } catch (e: Exception) {
            return
        }
        keys.forEach { k ->
            try {
                val raw = storage.read(k) ?: return@forEach
                if (parseEntry(raw, now) == null) storage.delete(k)
            } catch (e: Exception) {
            }
        }
    }

    private fun encodeEntry(body: String, expiresAt: Long): String = buildJsonObject {
        put("expiresAt", expiresAt)
        put("body", body)
    }.toString()
}

val apiCache = ResponseCache()
